// `capture_items` PUT dispatch for createItem.
//
// The adapter generates a UUID v4 client-side and INSERTs the optimistic
// capture_items row locally; this connector POSTs the same id to
// /capture/items so the server's primary key matches (createItemCore
// honours the supplied id). For a field-added asset type that re-homed
// onto an authoritative id, the local client id is remapped to that id
// before the POST: context.assetTypeIdRewrites for an in-batch re-home,
// falling back to the durable connector-asset-type-rewrite-registry for
// one that landed in an earlier batch (H19 #703).
//
// 5xx → throw (entry stays queued, retried). 4xx → throw, with two
// carve-outs that ack-and-drop instead of wedging the queue:
//
//   409 already_exists — the server already holds a row for this
//   (session, space, asset) at a DIFFERENT id. The backend raises this
//   for ANY such scope collision WITHOUT classifying the existing row
//   (assertNoScopeIdConflict), so the authoritative row may be a
//   quantity-0 template placeholder materialised when the space was
//   created OR a REAL count from another device / an earlier session.
//   details.authoritative carries that row (id + syncVersion + its own
//   quantity). The surveyor's offline count is the contractual baseline
//   (AGENTS.md "Business criticality"), so when THIS payload is itself a
//   meaningful capture — quantity > 0, or an explicit confirmed-zero —
//   we must NOT discard it: re-apply the captured quantity onto the
//   authoritative row via PUT /capture/items/:id at its syncVersion
//   (last-write-wins, ADR-003), THEN DELETE the local orphan and emit
//   `uploaded`. The updated row syncs back at the authoritative id, so
//   local SQLite ends with one row carrying the surveyor's count — not
//   the placeholder's 0.
//
//   Crucially we gate that re-apply on the MEANINGFULNESS of the local
//   payload, not on the authoritative row. The Add Asset flow enqueues
//   createItem with quantity 0 / isZeroConfirmed false purely to
//   REGISTER an asset type in the space (add-asset-dialog.tsx) — that is
//   not a count. Blindly PUTing that 0 onto an authoritative row holding
//   a real count would silently ZERO it — the same class of contractual
//   data-loss this file exists to prevent, and a regression vs. the old
//   orphan-drop (Codex P1 on this PR). So for a non-meaningful payload
//   we leave the server's value untouched and just drop the local
//   orphan; the authoritative row syncs back and the surveyor counts on
//   top of it. (Gating on the local payload — not "is the authoritative
//   a placeholder" — is deliberate: it preserves keep-my-count/LWW for a
//   genuine two-device clash where a real local count must win, while
//   still refusing to let a non-count clobber a real one.)
//
//   If the authoritative row was deleted in the meantime (PUT → 404
//   not_found) the count can never land: fall through to the same
//   durable-capture + quarantine path as the POST 404 case below.
//   Any other re-apply error throws (entry stays queued, retried); a
//   stale syncVersion (failed_precondition) converges because the
//   retry's POST re-reads the authoritative version. Historically this
//   branch just DELETEd the orphan and kept the placeholder, so an
//   offline count captured into a freshly-created room reverted to 0 on
//   reconnect with no notice — the silent contractual data-loss the
//   re-apply removes.
//
//   404 not_found — the referenced space or session does not exist on
//   the server, so this count's FK can never resolve and it will NEVER
//   upload as-is. uploadData dispatches the batch entry-by-entry and
//   only acks once ALL succeed, so throwing here wedges the ENTIRE
//   upload queue forever on this one entry — every later mutation for
//   every table is blocked behind it. This is the offline-count-upload
//   -wedge bug, and the "permanent error to be retried" MVP limitation
//   documented in connector-dispatch-session-insert.ts. The count lives
//   ONLY on this device, so a silent drop would be SILENT contractual
//   data-loss — instead we PRESERVE the full payload in the device-local
//   `orphaned_capture_items` quarantine (connector-quarantine.ts), THEN
//   remove the local orphan and ack so the queue drains. The count is
//   recoverable from the quarantine; we emit a `quarantined` event so a
//   UI affordance can tell the surveyor their offline count is held and
//   offer to re-inject it.
//
//   invalid_argument — a permanent client-validation 4xx: the asset type
//   is soft-rejected (admin Reject; I-API-8, #738 residual #2), the space
//   does not belong to the session's building, or a grouping rule failed.
//   None can succeed on retry and the count lives only on this device, so —
//   rather than wedge the queue (invalid_argument is permanent only for
//   capture_sessions in the central classifier, so a bare rethrow here would
//   wedge) — QUARANTINE the count and ack. A BROAD carve-out: every
//   count-bearing reachable cause is routed to not_found by the backend on
//   purpose so it quarantines, leaving invalid_argument as the
//   permanent-validation residue this preserves rather than drops.
//
// permission_denied and every other 4xx still throw — a permission_denied is
// a user-visible authorization error the surveyor can act on, not a moot row.

import type { CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'

import { ApiError, apiFetch, isNotFoundError } from '../api'

import { getAssetTypeRewrite } from './connector-asset-type-rewrite-registry'
import { quarantineUnsyncableCount } from './connector-quarantine'
import type { DispatchContext } from './connector-dispatch-context'
import { recoverSessionForItem } from './connector-dispatch-create-item-session-recovery'
import { recordCaptureItemRehome } from './connector-rehome-registry'
import { canonicalizeForUpload } from './quantity-canonical'
import { _emitUploaded } from './upload-events'
import {
  readAuthoritativeTarget,
  type CreateItemPostResult,
} from './connector-dispatch-create-item-response'
import { quantitiesEqual } from './connector-quantity-equal'

/** Coerce a CRUD opData value to a string column value, or null if absent. */
function str(value: unknown): string | null {
  return value === null || value === undefined ? null : String(value)
}

export async function dispatchCaptureItemInsert(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
): Promise<void> {
  const data = entry.opData ?? {}
  const queuedAssetTypeId = data['asset_type_id']
  const assetTypeId =
    typeof queuedAssetTypeId === 'string'
      ? // In-batch rewrite first (the field-add 409'd earlier in THIS batch),
        // then the durable cross-batch registry (the 409 landed in an EARLIER
        // batch — a >100-op offline session or a reload split the create from
        // this count; H19 #703). Without the durable fallback the count POSTs a
        // deleted id → 404 → stranded in quarantine. Same layering the session
        // re-home uses (in-batch sessionIdRewrites + cross-batch recovery).
        (context.assetTypeIdRewrites.get(queuedAssetTypeId) ??
        getAssetTypeRewrite(queuedAssetTypeId) ??
        queuedAssetTypeId)
      : queuedAssetTypeId
  // Re-home onto the authoritative session if the parent capture_sessions
  // PUT was re-homed earlier in this batch (connector-dispatch-session-
  // insert.ts). Without this the count POSTs against an orphan session
  // the server never had → 404 → silent drop of the surveyor's count.
  const queuedSessionId = data['capture_session_id']
  const sessionId =
    typeof queuedSessionId === 'string'
      ? (context.sessionIdRewrites.get(queuedSessionId) ?? queuedSessionId)
      : queuedSessionId
  await postCaptureItem(entry, db, context, data, assetTypeId, sessionId, true)
}

// POST the capture_items row, with a cross-batch session-recovery safety
// net layered on top of the in-batch sessionIdRewrites remap.
//
// Online, the session-409 re-home (connector-dispatch-session-insert.ts)
// and this item's upload almost never share a CRUD batch — uploadData
// runs once per batch with a FRESH DispatchContext (connector.ts), so the
// per-batch sessionIdRewrites map that saves the offline-reconnect case
// is empty here. The item then POSTs the orphan session id frozen into
// its queued op data → 404. Rather than drop the surveyor's count, we
// recover the building's live session the same way spaces/floors do
// (loadSessionIdForBuilding) and retry ONCE. By the time the item
// uploads, the authoritative session has synced down as the building's
// active session, so the retry lands and the count is preserved. The
// retry runs through this same handler with allowSessionRecovery=false so
// a placeholder under the recovered session still hits the 409 re-apply,
// and there is no second recovery (no infinite loop).
async function postCaptureItem(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext,
  data: Record<string, unknown>,
  assetTypeId: unknown,
  sessionId: unknown,
  allowSessionRecovery: boolean,
): Promise<void> {
  // Quarantine fidelity: the POST below attempts the REMAPPED assetTypeId /
  // sessionId, so a quarantined count must record THOSE, not the original
  // client ids frozen in `data` — else a later recovery would re-POST under ids
  // the server already deleted and 404 again. Both remaps must hold ACROSS CRUD
  // batches (a >100-op offline session / app reload splits the create from this
  // count, emptying the per-batch context): assetTypeId falls back to the
  // durable connector-asset-type-rewrite-registry (H18 #702 / H19 #703), and the
  // dead orphan session is recovered to the building's authoritative session in
  // the not_found branch below (H5 #701). (Codex P2 on #656)
  const quarantinePayload = {
    ...data,
    asset_type_id: assetTypeId,
    capture_session_id: sessionId,
  }
  // H21 #710: forward the device-stamped capture instant so the server
  // preserves the true offline-capture moment instead of overwriting
  // captured_at with reconnect-time defaultNow(). The skew grows the longer
  // the queue was offline/wedged, and corrupts CSV export column 16, the
  // dashboard timeseries, and the export's `ORDER BY capturedAt` row order.
  // The local row always carries an ISO-8601 captured_at (the adapter stamps
  // it on INSERT — capture-client-powersync-create-item.ts). Guard to a
  // string so a null/absent value is OMITTED (→ the backend's defaultNow()
  // for online REST + older app builds) rather than POSTed as null, which the
  // backend's optional (non-nullable) capturedAt schema would reject →
  // rethrow → device-queue wedge. CREATE PATH ONLY: the 409 re-apply PUT and
  // the increment PATCH never send captured_at (an update must not change it).
  const capturedAt = data['captured_at']
  // Meaningfulness of THIS payload (a real count vs the Add-Asset quantity-0
  // registration) gates every keep-my-count re-apply — identical to the 409
  // branch below. Hoisted so the POST body, the same-id LWW re-apply, and the
  // 409 different-id re-apply share one definition.
  const quantityNum = Number(data['quantity'])
  const isZeroConfirmed = data['is_zero_confirmed'] === 1
  const isMeaningfulCapture =
    (Number.isFinite(quantityNum) && quantityNum > 0) || isZeroConfirmed
  try {
    const created = await apiFetch<CreateItemPostResult>(`/capture/items`, {
      method: 'POST',
      json: {
        id: entry.id,
        sessionId,
        spaceId: data['space_id'] ?? null,
        assetTypeId,
        quantity: canonicalizeForUpload(data['quantity']),
        isZeroConfirmed,
        notes: data['notes'] ?? null,
        ...(typeof capturedAt === 'string' ? { capturedAt } : {}),
      },
    })
    // Same-id last-write-wins (audit-1 #2). A deterministic id (H11) makes two
    // writers capturing one slot send the SAME id, so the server resolves the
    // second as an idempotent 200 returning the STORED (first writer's) row.
    // Acking it as-is silently drops THIS surveyor's count — a first-write-wins
    // downgrade of the ADR-003 last-write-wins guarantee (Invariant 8). When
    // this payload is a meaningful capture whose quantity differs from the
    // returned row, re-apply it onto the authoritative row (PUT at its
    // syncVersion) — the same keep-my-count path the 409 (different-id) branch
    // uses, so same-id and different-id collisions resolve identically. No
    // orphan DELETE / re-home here: the row is already at entry.id (our id).
    //
    // Compare by VALUE, not text (quantitiesEqual): the server returns the
    // numeric(10,2) quantity SCALED ('4.00') while the local value is unscaled
    // ('4'), so a raw String()-compare would re-apply on EVERY idempotent
    // re-send. Each re-apply PUT spends a rate token, and a >60-item batch then
    // wedges forever on retry — silent capture loss. See connector-quantity-equal.ts.
    if (
      isMeaningfulCapture &&
      created !== null &&
      created.created === false &&
      created.item !== undefined &&
      // Compare against the CANONICAL local value (audit-1 #5's sibling, audit-2):
      // a device wedged before this fix holds a raw "5." draft, and
      // canonicalDecimal("5.") is null → quantitiesEqual would fall back to a
      // string compare ("5.00" !== "5.") → a NEEDLESS re-apply PUT (rate-token
      // waste, the class connector-quantity-equal.ts exists to kill). The POST/PUT
      // already send canonicalizeForUpload(...); the compare operand must match.
      // Re-apply on a quantity delta OR a confirmed-zero flag delta
      // (audit-1-#1). The flag leg is load-bearing for the deterministic-
      // template-id fix: a confirm-not-present tap in the holdback window
      // creates {quantity:'0', isZeroConfirmed:true} against the server's
      // qty-0 template row — the quantities MATCH, so a quantity-only gate
      // skipped the re-apply and the flag (ADR-021 contractual captured
      // work) silently reverted to false on the next checkpoint. The
      // different-id 409 branch below has no quantity gate and always
      // landed it; this keeps the two branches genuinely identical.
      (!quantitiesEqual(
        created.item.quantity,
        canonicalizeForUpload(data['quantity']),
      ) ||
        (created.item.isZeroConfirmed === true) !== isZeroConfirmed ||
        // Notes leg (audit-2-#1) — the last same-id/409 asymmetry: qty and
        // flag can both match while the note differs. Normalise both sides
        // to string|null (the backend's hydrate always returns string|null,
        // so an idempotent re-send compares equal — no rate-token burn).
        (typeof created.item.notes === 'string' ? created.item.notes : null) !==
          ((data['notes'] as string | null | undefined) ?? null))
    ) {
      const version =
        typeof created.item.syncVersion === 'number' ? created.item.syncVersion : 1
      try {
        await apiFetch(`/capture/items/${encodeURIComponent(entry.id)}`, {
          method: 'PUT',
          json: {
            quantity: canonicalizeForUpload(data['quantity']),
            isZeroConfirmed,
            notes: data['notes'] ?? null,
            syncVersion: version,
          },
        })
      } catch (reapplyErr) {
        if (isNotFoundError(reapplyErr)) {
          // Authoritative row deleted between the POST and our re-apply (an
          // Encore not_found OR a proxy/CDN code-less 404 — H3/#694) — the
          // count can never land as-is; preserve it in quarantine (never
          // silently drop) and ack so the queue drains.
          await quarantineUnsyncableCount(
            entry,
            db,
            reapplyErr,
            quarantinePayload,
            'powersync_capture_item_idempotent_reapply_not_found',
          )
          return
        }
        // Transient / stale-version (failed_precondition) — rethrow so the
        // entry stays queued and the retry re-reads the authoritative version.
        throw reapplyErr
      }
    }
    _emitUploaded(entry.table, entry.id)
    return
  } catch (err) {
    if (err instanceof ApiError && err.code === 'already_exists') {
      // The server already holds a row for this (session, space, asset)
      // at a DIFFERENT id. The backend raises already_exists for ANY
      // such scope collision without classifying the existing row, so
      // the authoritative row may be a quantity-0 template placeholder
      // OR a real count. See file header.
      const target = readAuthoritativeTarget(err)
      if (target === null) {
        // No authoritative payload to re-apply onto — we MUST NOT
        // silently drop the count, so surface the error (the entry
        // stays queued). This should not happen with the current backend.
        throw err
      }
      // Re-apply ONLY when this payload is itself a meaningful capture
      // (`isMeaningfulCapture`, hoisted above): quantity > 0, or an explicit
      // confirmed-zero. A non-meaningful payload (quantity 0 / not confirmed)
      // is the Add Asset registration shape (add-asset-dialog.tsx), NOT a
      // count — PUTing its 0 onto an authoritative row holding a real count
      // would silently zero it (Codex P1). For that case keep the server's
      // value and just drop the local orphan. See file header.
      // Track the authoritative row's version as the re-apply advances it,
      // so the orphan→authoritative rewrite recorded below carries the
      // right version for queued increments to re-home onto (see the
      // captureItemIdRewrites set just before the DELETE).
      let authoritativeSyncVersion = target.syncVersion
      if (isMeaningfulCapture) {
        try {
          const reapplied = await apiFetch<{ syncVersion?: number }>(
            `/capture/items/${encodeURIComponent(target.id)}`,
            {
              method: 'PUT',
              json: {
                quantity: canonicalizeForUpload(data['quantity']),
                isZeroConfirmed,
                notes: data['notes'] ?? null,
                syncVersion: target.syncVersion,
              },
            },
          )
          authoritativeSyncVersion =
            typeof reapplied?.syncVersion === 'number'
              ? reapplied.syncVersion
              : target.syncVersion + 1
        } catch (reapplyErr) {
          if (isNotFoundError(reapplyErr)) {
            // The authoritative row was deleted between the POST conflict and
            // our re-apply (or a code-less 404 from a proxy/CDN in front of
            // Encore — H3/#694; isNotFoundError covers both) — the count can
            // never land as-is. Preserve it in the
            // local quarantine (never silently drop a count — AGENTS.md
            // "Business criticality") and ack so the queue drains; the recovery
            // surface lets the surveyor re-inject it.
            await quarantineUnsyncableCount(
              entry,
              db,
              reapplyErr,
              quarantinePayload,
              'powersync_capture_item_already_exists_reapply_not_found',
            )
            return
          }
          // Any other re-apply failure (5xx, failed_precondition, etc.)
          // is transient or user-actionable — throw so the entry stays
          // queued and retries. A stale syncVersion converges: the next
          // POST re-reads the authoritative version. Never silently drop.
          throw reapplyErr
        }
      }
      // Either the re-apply landed (authoritative row now carries the
      // surveyor's count), or the payload was a non-meaningful
      // registration so the server's authoritative value stands
      // untouched. Either way the authoritative row is correct and syncs
      // back at its id. Drop the local orphan (its op=DELETE acks as a
      // no-op in dispatchCrudEntry) and emit `uploaded`.
      //
      // Re-home any increments the surveyor captured against this orphan:
      // each +/- tap was queued as its own capture_items PATCH pointing at
      // entry.id, which the DELETE below makes un-syncable (PUT → 404 →
      // dropped count, the silent contractual data-loss this re-home
      // closes). Recording orphan→authoritative lets dispatchCaptureItemPatch
      // redirect those increments onto the surviving row. Recorded in the
      // MODULE-LEVEL registry (not the per-batch context) so it survives
      // across CRUD batches — a >100-op offline session can split the create
      // from its increments into separate batches (Codex P1 on PR #630). See
      // connector-rehome-registry.ts.
      recordCaptureItemRehome(entry.id, {
        id: target.id,
        syncVersion: authoritativeSyncVersion,
        // H17: carry the authoritative row's FK/capture context so a queued
        // increment that must later be quarantined (the authoritative row was
        // deleted before it uploaded) records WHERE the count belongs instead of
        // NULL FKs — keeping it placeable by the recovery surface. Uses the SAME
        // remapped ids the POST used (sessionId / assetTypeId) — never the stale
        // client ids frozen in `data` — exactly the scope quarantinePayload
        // records for the create-path quarantines.
        context: {
          capture_session_id: str(sessionId),
          site_id: str(data['site_id']),
          building_id: str(data['building_id']),
          space_id: str(data['space_id']),
          asset_type_id: str(assetTypeId),
          captured_at: str(data['captured_at']),
        },
      })
      await db.execute(`DELETE FROM capture_items WHERE id = ?`, [entry.id])
      _emitUploaded(entry.table, entry.id)
      return
    }
    if (isNotFoundError(err)) {
      // Cross-batch session recovery (see postCaptureItem header). The FK
      // target is absent server-side. The common online cause is the orphan
      // session id frozen into this entry's op data after its parent session
      // 409-re-homed in an EARLIER batch. isNotFoundError also covers a code-less
      // 404 (a proxy/CDN 404 with no Encore code — H3/#694), so the count is
      // quarantined here with the REMAPPED `quarantinePayload` ids rather than
      // dropping to the backstop, which would quarantine the original (deleted)
      // ids and strand recovery. Recover the live session and retry
      // ONCE — this preserves the count instead of dropping it, covering the
      // online (multi-batch) variant the in-batch sessionIdRewrites map cannot
      // reach. Mirrors how spaces/floors resolve the session cross-batch
      // (connector-dispatch-space.ts / connector-dispatch-floor.ts).
      //
      // TRANSIENT-PROXY-404 TRADE-OFF (audit-2; open question for Rohan): if a
      // MISCONFIGURED proxy injects a 404 AFTER Encore already accepted this POST,
      // the row IS on the server yet we quarantine the count here — a FALSE-ALARM
      // "held counts" banner. It is NOT data loss (the authoritative row syncs back
      // down) and NOT a double-count (recovery writes to capture_recovery_dump, and
      // a same-id re-POST is idempotent), only a spurious notification. This is the
      // deliberate "drain+preserve ≫ wedge" stance (H3); removing the false alarm
      // would need server idempotency state the client can check BEFORE
      // quarantining — a product decision, not changed here.
      if (allowSessionRecovery) {
        const recovered = await recoverSessionForItem(
          db,
          context,
          data,
          sessionId,
        )
        if (recovered !== null && recovered !== sessionId) {
          return postCaptureItem(
            entry,
            db,
            context,
            data,
            assetTypeId,
            recovered,
            false,
          )
        }
      }
      // Recovery unavailable (no durable re-home, no live session for the
      // building, or the only live session is the one we already tried): the FK
      // target is genuinely absent and this count can never upload as-is.
      // Throwing would wedge the whole queue; instead preserve the count in the
      // local quarantine (never silently drop) and ack so the queue drains.
      await quarantineUnsyncableCount(
        entry,
        db,
        err,
        quarantinePayload,
        'powersync_capture_item_not_found_drop',
      )
      return
    }
    if (err instanceof ApiError && err.code === 'invalid_argument') {
      // A permanent client-validation 4xx on the count POST: createItemCore
      // raised invalid_argument because the referenced asset type is
      // soft-rejected (rejected_at set — the admin Reject the surveyor's
      // offline count was captured before; I-API-8, #738 residual #2), the
      // space does not belong to the session's building, or a grouping rule
      // failed. NONE can ever succeed on retry with the same payload, and the
      // count lives ONLY on this device. As of #751 invalid_argument IS in
      // PERMANENT_ITEM_CODES for capture_items, so the central backstop would
      // ALSO drain it — but this INLINE carve-out fires first (it returns before
      // the classifier is consulted), making it the first-line drain and the
      // backstop defence-in-depth. (Before #751 the backstop rethrew it, so this
      // carve-out was the SOLE thing keeping the bare `throw err` below from
      // wedging the WHOLE device upload queue behind this one count — the #656
      // single-ack class.) This is a BROAD carve-out by
      // design: every count-BEARING reachable cause is deliberately routed to
      // not_found by the backend precisely so it quarantines (an absent FK, an
      // admin-excluded pairing — see create-item.ts / ADR-024), leaving
      // invalid_argument as the permanent-validation residue with no count we
      // could land — so quarantining the whole family is safe and preserves the
      // count for the recovery surface rather than dropping it. Reuse the
      // already-remapped quarantinePayload so a later recovery re-POSTs the
      // authoritative asset-type / session ids, not the dead client orphans.
      // NOTE: gated strictly on invalid_argument — permission_denied (a session
      // the role may not write) intentionally still throws below, so it is NOT
      // swallowed here.
      await quarantineUnsyncableCount(
        entry,
        db,
        err,
        quarantinePayload,
        'powersync_capture_item_invalid_argument_quarantine',
      )
      return
    }
    throw err
  }
}
